import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { UserPlus, Calendar, Users, Building } from 'lucide-react';
import { useClients } from '@/hooks/useClients';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface EventClientAssignmentProps {
  eventId: string;
  eventTitle: string;
  currentClientIds?: string[];
  onAssignmentUpdate?: () => void;
}

export const EventClientAssignment: React.FC<EventClientAssignmentProps> = ({ 
  eventId, 
  eventTitle, 
  currentClientIds = [], 
  onAssignmentUpdate 
}) => {
  const { clients, loading } = useClients();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedClientIds, setSelectedClientIds] = useState<string[]>(currentClientIds);
  const [saving, setSaving] = useState(false);
  const [assignments, setAssignments] = useState<any[]>([]);

  // Load current assignments
  useEffect(() => {
    const loadAssignments = async () => {
      const { data } = await supabase
        .from('events')
        .select('*')
        .eq('id', eventId)
        .single();
      
      if (data && data.client_id) {
        setSelectedClientIds([data.client_id]);
      }
    };
    
    loadAssignments();
  }, [eventId]);

  const handleClientToggle = (clientId: string, checked: boolean) => {
    setSelectedClientIds(prev => 
      checked 
        ? [...prev, clientId]
        : prev.filter(id => id !== clientId)
    );
  };

  const handleSaveAssignments = async () => {
    setSaving(true);
    try {
      // For now, just update the event's client_id field with the first selected client
      // This is a simplified approach since the table structure supports one client per event
      const clientId = selectedClientIds.length > 0 ? selectedClientIds[0] : null;
      
      await supabase
        .from('events')
        .update({ client_id: clientId })
        .eq('id', eventId);

      toast.success('Client assignment updated successfully');
      setDialogOpen(false);
      onAssignmentUpdate?.();
    } catch (error) {
      toast.error('Failed to update client assignment');
      console.error('Error updating assignment:', error);
    } finally {
      setSaving(false);
    }
  };

  const assignedClients = clients.filter(client => currentClientIds.includes(client.id));

  return (
    <div className="space-y-4">
      {/* Current Assignments Display */}
      {assignedClients.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium flex items-center gap-2">
              <Users className="h-4 w-4" />
              Assigned Client
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {assignedClients.map((client) => (
                <Badge key={client.id} variant="secondary" className="flex items-center gap-1">
                  <Building className="h-3 w-3" />
                  {client.name}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Assignment Management Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogTrigger asChild>
          <Button variant="outline" size="sm">
            <UserPlus className="h-4 w-4 mr-2" />
            {assignedClients.length > 0 ? 'Change Client Assignment' : 'Assign to Client'}
          </Button>
        </DialogTrigger>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Assign Event to Client</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="flex items-center gap-2 p-3 bg-muted/50 rounded">
              <Calendar className="h-4 w-4 text-primary" />
              <div>
                <p className="font-medium text-sm">{eventTitle}</p>
                <p className="text-xs text-muted-foreground">Select which client should have access</p>
              </div>
            </div>

            {loading ? (
              <div className="text-center py-4">Loading clients...</div>
            ) : clients.length === 0 ? (
              <div className="text-center py-8">
                <Users className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground">No clients available</p>
                <p className="text-sm text-muted-foreground">Create clients first to assign them to events</p>
              </div>
            ) : (
              <div className="space-y-3 max-h-64 overflow-y-auto">
                <div className="flex items-center space-x-3 p-3 rounded border border-border/50 hover:bg-muted/50">
                  <Checkbox
                    checked={selectedClientIds.length === 0}
                    onCheckedChange={(checked) => {
                      if (checked) {
                        setSelectedClientIds([]);
                      }
                    }}
                  />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm">No Client Assignment</p>
                    <p className="text-xs text-muted-foreground">Remove client assignment from this event</p>
                  </div>
                </div>
                {clients.map((client) => (
                  <div key={client.id} className="flex items-center space-x-3 p-3 rounded border border-border/50 hover:bg-muted/50">
                    <Checkbox
                      checked={selectedClientIds.includes(client.id)}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          setSelectedClientIds([client.id]); // Only allow one client
                        } else {
                          setSelectedClientIds([]);
                        }
                      }}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-sm">{client.name}</p>
                        {client.company && (
                          <Badge variant="outline" className="text-xs">
                            {client.company}
                          </Badge>
                        )}
                      </div>
                      {client.email && (
                        <p className="text-xs text-muted-foreground">{client.email}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="flex gap-2 pt-4">
              <Button 
                onClick={handleSaveAssignments} 
                disabled={saving}
                className="flex-1"
              >
                {saving ? 'Saving...' : 'Save Assignment'}
              </Button>
              <Button 
                variant="outline" 
                onClick={() => setDialogOpen(false)}
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EventClientAssignment;